import React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { AuthLayout } from '../catalyst/auth-layout'
import { Login } from '../pages/auth/Login'
import { Register } from '../pages/auth/Register'
import { ForgotPassword } from '../pages/auth/ForgotPassword'
import { AcceptInvitation } from '../components/pages/AcceptInvitation'
import { setBrowserRouteName } from '../lib/newRelicBrowser'
import { MountSignal } from '../lib/mountSignal'
import { MotionProvider } from '../lib/motionProvider'

const PAGES: Record<string, React.ComponentType<any>> = {
  Login: Login,
  Register: Register,
  ForgotPassword: ForgotPassword,
  AcceptInvitation: AcceptInvitation,
}

let root: Root | undefined
let rootElement: HTMLElement | undefined

const unmountApp = () => {
  root?.unmount()
  root = undefined
  rootElement = undefined
}

const mountApp = () => {
  const rootEl = document.getElementById('auth-root')
  if (!rootEl) return

  if (root && rootElement === rootEl) return
  unmountApp()

  const props = JSON.parse(rootEl.dataset.props || '{}')
  const pageName = rootEl.dataset.page || 'Login'
  setBrowserRouteName(pageName)
  const PageComponent =
    PAGES[pageName] ||
    (() => <div className="p-4 text-red-500">Page component '{pageName}' not found.</div>)

  const railsContentEl = document.getElementById('rails-content')
  const railsContent = railsContentEl?.innerHTML

  // If we have rails content, remove the original element to prevent duplication
  if (railsContentEl) {
    railsContentEl.remove()
  }

  root = createRoot(rootEl)
  rootElement = rootEl

  if (railsContent) {
    root.render(
      <MotionProvider>
        <div dangerouslySetInnerHTML={{ __html: railsContent }} />
        <MountSignal target={rootEl} />
      </MotionProvider>,
    )
  } else {
    root.render(
      <MotionProvider>
        <AuthLayout>
          <PageComponent {...props} />
        </AuthLayout>
        <MountSignal target={rootEl} />
      </MotionProvider>,
    )
  }
}

// Turbo does not fire turbo:load after a form submission that responds with
// an error status - only turbo:render (see formSubmissionFailedWithResponse
// in @hotwired/turbo, which renders the error response directly without
// going through the Visit lifecycle that turbo:load comes from). Without a
// turbo:render listener, a failed sign-in/registration submission left this
// page showing stale pre-submit props, silently swallowing the real error.
document.addEventListener('turbo:before-cache', unmountApp)
document.addEventListener('turbo:before-render', unmountApp)
document.addEventListener('turbo:render', mountApp)
document.addEventListener('turbo:load', mountApp)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountApp)
} else {
  mountApp()
}
